import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Check } from "lucide-react";

const TIERS = [
  {
    id: "free",
    name: "Free",
    tagline: "For the core 4",
    price: null,
    priceLabel: "$0",
    subLabel: "free",
    bullets: [
      "Up to 4 members",
      "Unlimited check-ins",
      "14-day mood & sleep history",
      "Family calendar — 50 events at a time",
      "Walk log + care log for 1 dog or cat",
      "Feeds & fresh water for every pet",
    ],
    packages: [],
  },
  {
    id: "plus",
    name: "Plus",
    tagline: "Add more kids, pets or a grandparent",
    bullets: [
      "Up to 6 members",
      "Everything in Free",
      "Full mood & sleep history",
      "Custom photo avatars",
      "Photo attachment on events",
      "Unlimited calendar events",
      "Sync Google, iCloud & Outlook calendars",
      "Walk log + care log for 2 dogs or cats",
      "Birthday & pet-care push reminders",
      "Start with 14 days free",
    ],
    packages: ["plus_monthly", "plus_yearly"],
    highlight: true,
  },
  {
    id: "grand",
    name: "Grand",
    tagline: "For big houses & pet packs",
    bullets: [
      "Up to 10 members",
      "Everything in Plus",
      "Walk log + care log for 3 dogs or cats",
      "Houses (Mum's / Dad's / Grandparents')",
      "Start with 14 days free",
    ],
    packages: ["grand_monthly", "grand_yearly"],
  },
  {
    id: "extended",
    name: "Extended Family",
    tagline: "Generations, a small zoo or co-parenting",
    bullets: [
      "Up to 20 members",
      "Everything in Grand",
      "Walk log + care log for every dog & cat",
      "For very large blended / multi-generational households",
    ],
    packages: ["extended_monthly", "extended_yearly"],
  },
];

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function BillingSheet({ open, onClose, reason = null, onUpgraded }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // package_id currently being processed
  const [interval, setInterval] = useState("year"); // month | year toggle

  const load = useCallback(async () => {
    try {
      const s = await api.billingStatus();
      setStatus(s);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setInterval("year");
    }
  }, [open, load]);

  if (!open) return null;

  const packageMap = Object.fromEntries((status?.packages || []).map((p) => [p.id, p]));
  const currentTier = status?.tier || "free";
  const memberCount = status?.member_count ?? 0;

  const startCheckout = async (package_id) => {
    setBusy(package_id);
    try {
      const origin = window.location.origin;
      const { url } = await api.billingCheckout({ package_id, origin_url: origin });
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Couldn't start checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Your plan stays active until the end of the current paid period and then lapses back to Free. Cancel renewal?")) return;
    setBusy("cancel");
    try {
      const s = await api.billingCancel();
      setStatus(s);
      toast.success("Renewal cancelled");
    } catch {
      toast.error("Couldn't cancel");
    } finally {
      setBusy(null);
    }
  };

  const TIER_INDEX = { free: 0, plus: 1, grand: 2, extended: 3 };

  const handleDowngrade = async (targetTierId, targetTierName) => {
    const curIdx = TIER_INDEX[currentTier] ?? 0;
    const isCurrentExtended = currentTier === "extended";
    const losingHouses = isCurrentExtended && targetTierId !== "extended";
    const targetCap =
      { free: 4, plus: 6, grand: 10, extended: 20 }[targetTierId] || 4;
    const willTrim = memberCount > targetCap
      ? `Your ${memberCount} members will be trimmed to ${targetCap} — the last ${memberCount - targetCap} you added get removed (their check-ins go with them).`
      : null;
    const lines = [
      `Downgrade from ${currentTier.toUpperCase()} to ${targetTierName}?`,
      losingHouses ? "• Every House will be deleted (members land on the shared front)." : null,
      losingHouses ? "• Events scoped to a house will be permanently deleted." : null,
      willTrim ? `• ${willTrim}` : null,
      targetTierId === "free"
        ? "• Your paid subscription will cancel at the end of the current period, but the cleanup above happens now."
        : "• We'll send you to Stripe to confirm the plan swap after the cleanup.",
    ].filter(Boolean);
    if (!window.confirm(lines.join("\n\n"))) return;
    setBusy(`downgrade-${targetTierId}`);
    try {
      const res = await api.billingDowngrade({ target_tier: targetTierId });
      setStatus(res);
      if (res?.portal_url) {
        toast.success("Opening Stripe to confirm plan swap");
        window.location.href = res.portal_url;
        return;
      }
      toast.success(`Dropped to ${targetTierName}${res?.removed_members ? ` · ${res.removed_members} member(s) removed` : ""}`);
      onUpgraded && onUpgraded(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't downgrade");
    } finally {
      setBusy(null);
    }
    // Reference so eslint doesn't flag curIdx
    return curIdx;
  };

  const handleResume = async () => {
    setBusy("resume");
    try {
      const s = await api.billingResume();
      setStatus(s);
      toast.success("Plan will keep auto-renewing");
    } catch {
      toast.error("Couldn't resume");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="billing-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-4xl max-h-[100vh] md:max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div>
            <div className="label-tiny">Plans &amp; billing</div>
            <h2 className="font-display-italic text-3xl md:text-4xl leading-none mt-1 tracking-tight">
              Grow the family
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            data-testid="billing-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="px-5 md:px-7 py-5 space-y-6">
          {reason && (
            <div
              className="ff-card p-4 flex items-start gap-3 bg-[color:var(--accent-soft,#FFE8A1)]/60"
              data-testid="billing-reason"
            >
              <div className="text-2xl leading-none">🫶</div>
              <div className="flex-1">
                <div className="font-display text-base tracking-tight leading-tight">
                  {reason.title || "Upgrade required"}
                </div>
                <div className="label-tiny mt-1 text-[color:var(--ink-2)]">
                  {reason.message ||
                    `Your ${currentTier} plan is full. Pick a plan below to add more members.`}
                </div>
                {(() => {
                  // Once status is loaded, surface the suggested tier's actual
                  // monthly price + member cap right in the banner so the user
                  // sees the cost before scanning all four plan cards.
                  const sug = reason.suggested_tier;
                  if (!sug || !status) return null;
                  const tierMeta = TIERS.find((t) => t.id === sug);
                  const monthlyPkgId = `${sug}_monthly`;
                  const monthlyPkg = packageMap[monthlyPkgId];
                  if (!tierMeta || !monthlyPkg) return null;
                  const cap =
                    { plus: 6, grand: 10, extended: 20 }[sug] || null;
                  return (
                    <div
                      className="label-tiny mt-2 text-[color:var(--ink)]"
                      data-testid="billing-reason-price"
                    >
                      {tierMeta.name} is{" "}
                      <span className="font-medium">
                        ${monthlyPkg.amount.toFixed(2)}/mo
                      </span>
                      {cap ? ` for up to ${cap} members` : ""} —{" "}
                      <button
                        type="button"
                        onClick={() => startCheckout(monthlyPkgId)}
                        disabled={busy === monthlyPkgId}
                        className="underline hover:text-[color:var(--ink-2)] disabled:opacity-60"
                        data-testid="billing-reason-upgrade"
                      >
                        {busy === monthlyPkgId
                          ? "opening Stripe…"
                          : `upgrade now`}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {status && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="label-tiny text-[color:var(--ink-2)]">
                  CURRENT: <span className="text-[color:var(--ink)]">{currentTier.toUpperCase()}</span>
                  {" · "}{memberCount}/{status.max_members} members
                </span>
                {(() => {
                  // Quiet trial pill — only renders when Stripe says the
                  // subscription is still in its 14-day trial window. Format:
                  // "Trial · 11/14 days" so the family knows where they are.
                  if (status.subscription_status !== "trialing") return null;
                  if (!status.trial_ends_at) return null;
                  const endMs = new Date(status.trial_ends_at).getTime();
                  if (Number.isNaN(endMs)) return null;
                  const msLeft = endMs - Date.now();
                  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
                  const daysUsed = Math.min(14, Math.max(0, 14 - daysLeft));
                  return (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-[color:var(--bg-soft,#F5EFE2)] border border-[color:var(--line-soft)] text-[color:var(--ink-2)] text-[11px] uppercase tracking-[0.08em] px-2 py-0.5"
                      title={`Free trial — converts to paid on ${fmtWhen(status.trial_ends_at)}`}
                      data-testid="billing-trial-pill"
                    >
                      Trial · {daysUsed}/14 days
                    </span>
                  );
                })()}
                {status.is_active && status.expires_at && (
                  <span className="label-tiny text-[color:var(--ink-2)]">
                    · {status.cancel_at_period_end
                      ? `ends ${fmtWhen(status.expires_at)}`
                      : status.auto_renew
                      ? `renews ${fmtWhen(status.expires_at)}`
                      : `active until ${fmtWhen(status.expires_at)}`}
                  </span>
                )}
              </div>
              {status.is_active && status.admin_override && (
                <div className="label-tiny text-[color:var(--ink-3)] leading-snug">
                  Complimentary access — your plan continues automatically and
                  won't lapse.
                </div>
              )}
              {status.is_active && status.auto_renew && !status.cancel_at_period_end && !status.admin_override && (
                <div className="flex items-center gap-2">
                  <div className="label-tiny text-[color:var(--ink-3)] flex-1">
                    Plan auto-renews on {fmtWhen(status.expires_at)}.
                  </div>
                  <button
                    onClick={handleCancel}
                    disabled={busy === "cancel"}
                    className="ff-chip"
                    data-testid="billing-cancel-btn"
                  >
                    {busy === "cancel" ? "Cancelling…" : "Cancel renewal"}
                  </button>
                </div>
              )}
              {status.is_active && status.cancel_at_period_end && !status.admin_override && (
                <div className="flex items-center gap-2">
                  <div className="label-tiny text-[color:var(--ink-3)] flex-1">
                    Your plan will end on {fmtWhen(status.expires_at)} and not auto-renew.
                  </div>
                  <button
                    onClick={handleResume}
                    disabled={busy === "resume"}
                    className="ff-chip"
                    data-testid="billing-resume-btn"
                  >
                    {busy === "resume" ? "Resuming…" : "Keep my plan"}
                  </button>
                </div>
              )}
              {status.is_active && !status.has_subscription && !status.admin_override && (
                <div className="flex items-start gap-2">
                  <div className="label-tiny text-[color:var(--ink-3)] flex-1 leading-snug">
                    Your current plan doesn't auto-renew — it was started before
                    auto-renewal was available. Pick a plan below to switch to
                    monthly or yearly auto-renew, or leave it and it'll lapse
                    back to Free on {fmtWhen(status.expires_at)}.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Interval toggle */}
          <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="billing-interval-toggle"
          >
            <button
              onClick={() => setInterval("month")}
              className={`ff-chip ${interval === "month" ? "is-active" : ""}`}
              data-testid="interval-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("year")}
              className={`ff-chip ${interval === "year" ? "is-active" : ""}`}
              data-testid="interval-yearly"
            >
              Yearly <span className="text-[color:var(--ink-3)] ml-1">· save 20%</span>
            </button>
            <span
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase text-[color:var(--ink-3)] ml-auto"
              title="Stripe auto-converts USD to your local currency at checkout"
              data-testid="billing-local-currency-chip"
            >
              <span aria-hidden="true">🌍</span>
              Local currency at checkout
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIERS.map((t) => {
              const pkgId = t.packages.find((p) =>
                interval === "month" ? p.endsWith("_monthly") : p.endsWith("_yearly")
              );
              const pkg = pkgId ? packageMap[pkgId] : null;
              const isCurrent = currentTier === t.id;
              const priceLabel = t.id === "free"
                ? "$0"
                : pkg
                ? `$${pkg.amount.toFixed(2)}`
                : "—";
              const subLabel = t.id === "free"
                ? "free"
                : interval === "month"
                ? "/month"
                : "/year";
              // Yearly savings: monthly_amount * 12 - yearly_amount, rounded
              // to the nearest dollar. Only shown on yearly view for paid tiers.
              let savingsLabel = null;
              if (interval === "year" && t.id !== "free") {
                const monthlyPkg = packageMap[`${t.id}_monthly`];
                const yearlyPkg = packageMap[`${t.id}_yearly`];
                if (monthlyPkg && yearlyPkg) {
                  const saved = monthlyPkg.amount * 12 - yearlyPkg.amount;
                  if (saved > 0.5) {
                    savingsLabel = `Save $${Math.round(saved)}/yr`;
                  }
                }
              }
              return (
                <div
                  key={t.id}
                  className={`ff-card p-5 flex flex-col gap-3 ${t.highlight ? "ring-1 ring-[color:var(--ink)]/30" : ""}`}
                  data-testid={`plan-card-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-xl tracking-tight">{t.name}</div>
                      <div className="label-tiny text-[color:var(--ink-2)] mt-0.5">{t.tagline}</div>
                      {t.id === "plus" && status?.plus_trial_eligible && (
                        <div
                          className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[color:var(--ink)]/8 text-[color:var(--ink)]"
                          data-testid="plan-plus-trial-badge"
                        >
                          14 days free
                        </div>
                      )}
                      {t.id === "grand" && status?.grand_trial_eligible && (
                        <div
                          className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[color:var(--ink)]/8 text-[color:var(--ink)]"
                          data-testid="plan-grand-trial-badge"
                        >
                          14 days free
                        </div>
                      )}
                    </div>
                    {isCurrent && (
                      <span className="ff-chip is-active" data-testid={`plan-${t.id}-current`}>
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-4xl tracking-tighter">{priceLabel}</span>
                    <span className="label-tiny text-[color:var(--ink-2)]">{subLabel}</span>
                  </div>
                  <ul className="space-y-1.5 pt-1">
                    {t.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm leading-tight">
                        <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0" strokeWidth={2} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {savingsLabel && (
                    <div
                      className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 rounded-full bg-[color:var(--accent-soft,#FFE8A1)]/70 border border-[color:var(--accent,#D4A574)]/40 text-[color:var(--ink)] text-[11px] uppercase tracking-[0.06em] font-semibold mt-1"
                      data-testid={`plan-${t.id}-yearly-savings`}
                      title={`Yearly billing saves you $${savingsLabel.match(/\d+/)?.[0] || ""} compared with paying monthly.`}
                    >
                      <span aria-hidden="true">💰</span>
                      {savingsLabel}
                    </div>
                  )}
                  <div className="pt-2 mt-auto">
                    {(() => {
                      const curIdx = TIER_INDEX[currentTier] ?? 0;
                      const targetIdx = TIER_INDEX[t.id] ?? 0;
                      const isDowngrade = !isCurrent && targetIdx < curIdx;
                      if (t.id === "free") {
                        if (isDowngrade) {
                          return (
                            <button
                              onClick={() => handleDowngrade("free", t.name)}
                              disabled={busy === "downgrade-free"}
                              className="ff-btn w-full"
                              data-testid={`plan-${t.id}-downgrade`}
                            >
                              {busy === "downgrade-free"
                                ? "Working…"
                                : "Downgrade to Free"}
                            </button>
                          );
                        }
                        return (
                          <span className="label-tiny text-[color:var(--ink-3)]">
                            {isCurrent ? "Current plan" : "Default — no payment needed"}
                          </span>
                        );
                      }
                      if (isDowngrade) {
                        return (
                          <button
                            onClick={() => handleDowngrade(t.id, t.name)}
                            disabled={busy === `downgrade-${t.id}`}
                            className="ff-btn w-full"
                            data-testid={`plan-${t.id}-downgrade`}
                          >
                            {busy === `downgrade-${t.id}`
                              ? "Working…"
                              : `Downgrade to ${t.name}`}
                          </button>
                        );
                      }
                      // Active tier — show a non-interactive
                      // "Current plan" pill instead of a button.
                      // Stripe subs auto-renew, so a second checkout
                      // here would either duplicate the subscription
                      // or confuse the customer; the safer pattern
                      // is "you're on this; nothing to do."
                      if (isCurrent) {
                        return (
                          <div
                            className="w-full text-center px-5 py-3 rounded-full bg-[color:var(--bg-soft)] border border-[color:var(--line)] text-[color:var(--ink-2)]"
                            data-testid={`plan-${t.id}-current`}
                          >
                            <span className="label-tiny">Current plan</span>
                            <span className="block text-[11px] text-[color:var(--ink-3)] mt-0.5 normal-case tracking-normal">
                              Renews automatically
                            </span>
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => pkgId && startCheckout(pkgId)}
                          disabled={!pkgId || busy === pkgId}
                          className={`ff-btn ${t.highlight ? "ff-btn-primary" : ""} w-full`}
                          data-testid={`plan-${t.id}-checkout`}
                        >
                          {busy === pkgId
                            ? "Opening Stripe…"
                            : t.id === "plus" && status?.plus_trial_eligible
                              ? "Start 14-day free trial"
                              : t.id === "grand" && status?.grand_trial_eligible
                                ? "Start 14-day free trial"
                                : `Upgrade to ${t.name}`}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="label-tiny text-[color:var(--ink-3)] pt-2">
            Prices shown in USD. Stripe auto-converts to your local currency at checkout. Cancel anytime — your plan stays active until the end of the paid period.
            {onUpgraded ? "" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
